import { useEffect } from "react";
import { useSelector } from "react-redux";
import makeStyles from "@mui/styles/makeStyles";
import { ZoomControl } from "react-mapbox-gl";

import { driverTabs } from "../../consts/tabs.js";
import { DRIVER_MANAGEMENT } from "../../consts/routes.js";

import { useDepotMap } from "../../hooks/useDepotMap.js";

import {
  selectedDriverSelector,
  selectedTabSelector,
} from "../../reducers/ownApp/selectors.js";
import {
  driverEditModeSelector,
  hideDriverManagementModalSelector,
  selectingDriverLocationSelector,
  driverProfileEditSelector,
} from "../../reducers/driverManagement/selectors.js";
import { getFitBounds } from "../../reducers/map/selectors.js";

import { Layers } from "../../components/Layers/Layers.jsx";
import { Sources } from "../../components/Sources.jsx";
import { LoadingBackdrop } from "../../components/LoadingBackdrop.jsx";
import Header from "../../components/Header/index.jsx";
import { Subheader } from "../../components/Subheaders/DriverManagement/index.jsx";
import { View } from "../../components/View.jsx";
import { AppSections } from "../../components/LeftMenu/index.jsx";
import Mapbox from "../../components/Mapbox.js";
import WorkScheduleModal from "../../components/Modals/WorkScheduleModal/WorkScheduleModal.jsx";
import DriverProfileModal from "../../components/Modals/DriverProfileModal/DriverProfileModal.jsx";
import FilterPanel from "../../components/FilterPanel/FilterPanel.jsx";
import { DriverManagementExpiredModal } from "../../components/Modals/DriverManagementExpired/DriverManagementExpiredModal.jsx";
import UnlockMatrixWeekModal from "../../components/Modals/UnlockMatrixWeekModal/UnlockMatrixWeekModal.jsx";
import UnlockDriversWeekModal from "../../components/Modals/UnlockDriversWeekModal/UnlockDriversWeekModal.jsx";
import { AddDriverModal } from "../../components/Modals/AddDriver/AddDriverModal.jsx";
import { DriverManagementReloadModal } from "../../components/Modals/DriverManagementReload/DriverManagementReloadModal.jsx";
import { SelectDriverLocationModal } from "../../components/Modals/SelectDriverLocation/index.jsx";
import { HomeDepotController } from "../../components/HomeDepotController.jsx";
import { StreetViewController } from "../../components/StreetViewController.jsx";

const useStyles = makeStyles({
  appContainer: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    maxWidth: "100vw",
    maxHeight: "100vh",
  },
  content: {
    flexGrow: 1,
    position: "relative",
    display: "flex",
    overflow: "hidden",
    height: "calc(100% - 70px)",
  },
  left: {
    display: "flex",
  },
  right: {
    flexGrow: 1,
    position: "relative",
    display: "flex",
    flexDirection: "column",
  },
  mapContainer: {
    flexGrow: 1,
  },
  modalContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
});

const DriverManagement = () => {
  const styles = useStyles();
  const fitBounds = useSelector(getFitBounds);
  const selectedTab = useSelector(selectedTabSelector);
  const selectedDriver = useSelector(selectedDriverSelector);
  const driverEditMode = useSelector(driverEditModeSelector);
  const hideDriverManagementModal = useSelector(
    hideDriverManagementModalSelector
  );
  const selectingDriverLocation = useSelector(selectingDriverLocationSelector);
  const driverProfileEdit = useSelector(driverProfileEditSelector);

  useEffect(() => {
    document.title = "Driver - Quantum Management";
  }, []);

  const { map, handleOnLoad } = useDepotMap();

  // Fixes issue of map not resizing when switching tabs
  useEffect(() => {
    if (map) map.resize();
  }, [selectedTab, map]);

  // This forces map to rerender when driver location changes
  useEffect(() => {
    if (map && driverProfileEdit) map.zoomTo(map.getZoom());
  }, [driverProfileEdit, map]);

  return (
    <View className={styles.appContainer}>
      <LoadingBackdrop />

      <Header>
        <Subheader />
      </Header>

      <View className={styles.content}>
        <View className={styles.left}>
          <AppSections />
        </View>

        <View className={styles.right}>
          <View className={styles.mapContainer}>
            <Mapbox
              application={DRIVER_MANAGEMENT}
              fitBounds={fitBounds}
              handleOnLoad={handleOnLoad}
            >
              <Sources map={map} />
              <Layers map={map} />
              <ZoomControl position={"bottom-right"} />
              <HomeDepotController />
              <StreetViewController />
            </Mapbox>
          </View>

          {!hideDriverManagementModal &&
            (selectedDriver || driverEditMode) &&
            !selectingDriverLocation && (
              <View className={styles.modalContainer}>
                <DriverProfileModal
                  hidden={selectedTab !== driverTabs[0].label}
                />
                <WorkScheduleModal
                  hidden={selectedTab !== driverTabs[1].label}
                />
              </View>
            )}
          {selectingDriverLocation && (
            <SelectDriverLocationModal
              selectingDriverLocation={selectingDriverLocation}
            />
          )}
        </View>
        <FilterPanel />
        <AddDriverModal />
        <DriverManagementReloadModal />
        <DriverManagementExpiredModal />
        <UnlockMatrixWeekModal />
        <UnlockDriversWeekModal />
      </View>
    </View>
  );
};

export default DriverManagement;
